import { Link } from "react-router-dom";
import { Badge, buttonClass, Callout, Card, Stepper } from "@promptgatewaysadmin/ui";
import { useSubscriptionState } from "../queries/hooks.js";
import { ErrorState, OfflineBanner, Skeleton } from "../components/states.js";
import { HandoffButton } from "../components/HandoffButton.js";
import { DateText } from "../components/format.js";
import { daysLeft, type SubscriptionState } from "../api/normalise.js";
import { getPlan } from "../pricing.js";
import { config } from "../config.js";

/**
 * /subscription — read-only state plus intent CTAs.
 *
 * billing.v1.yaml exposes exactly four caller-facing operations: create a checkout session,
 * create a portal link, read the subscription, list invoices. There is NO endpoint to change
 * seats, switch plan, change interval, update a card, or cancel — and the service states
 * outright that it stores no card data.
 *
 * So: no seat stepper that writes, no plan switcher, no billing-cycle control, no in-app
 * cancel, and no card form. Current values still RENDER, disabled with an affordance, with
 * the Stripe handoff adjacent.
 *
 * This reopens only if Billing adds both PATCH /v1/billing/subscription AND a
 * proration-preview endpoint. The preview is what would make a native stepper better than a
 * handoff; without it, a stepper is a worse Stripe.
 */
export function Subscription() {
  const sub = useSubscriptionState();

  if (sub.isError) {
    return (
      <div className="pg-page pg-page--narrow">
        <ErrorState error={sub.error} fallbackKey="error.billing.load" onRetry={() => void sub.refetch()} />
      </div>
    );
  }
  if (sub.isPending) {
    return (
      <div className="pg-page pg-page--narrow">
        <Skeleton height={220} />
      </div>
    );
  }

  const state = sub.data;
  const plan = getPlan(state.plan);

  return (
    <div className="pg-page pg-page--narrow">
      <OfflineBanner />

      <Card title={plan.displayName} eyebrow="Your plan">
        <StatusBadge state={state} />

        <p className="pg-card__body">{plan.blurb}</p>

        <Stepper
          label="Seats"
          value={state.seatLimit}
          readOnlyReason="Seats are changed in Stripe, where the proration is calculated and shown before you confirm."
        />

        <dl className="pg-deflist">
          <dt>Billing cycle</dt>
          <dd>{state.interval === "year" ? "Annual" : state.interval === "month" ? "Monthly" : "—"}</dd>
          {state.currentPeriodEnd && (
            <>
              <dt>{state.cancelAtPeriodEnd ? "Ends" : "Renews"}</dt>
              <dd>
                <DateText iso={state.currentPeriodEnd} />
              </dd>
            </>
          )}
          {state.status === "trialing" && state.trialEndsAt && (
            <>
              <dt>Trial ends</dt>
              <dd>
                <DateText iso={state.trialEndsAt} /> ({daysLeft(state.trialEndsAt)} days left)
              </dd>
            </>
          )}
        </dl>
      </Card>

      <Card title="Change your plan">
        {state.status === "trialing" || state.status === "locked" || state.status === "canceled" ? (
          <>
            <p className="pg-card__body">
              {state.status === "trialing"
                ? "No card on file. Nothing is charged until you choose a plan."
                : "Nothing was charged. Pick a plan and everything picks up where it left off."}
            </p>
            <Link className={buttonClass("primary")} to="/checkout">
              Choose a plan
            </Link>
          </>
        ) : (
          <>
            <p className="pg-card__body">
              Seats, plan and billing cycle are all managed in Stripe, where the proration is
              shown before you confirm.
            </p>
            <div className="pg-form__actions">
              <HandoffButton label="Manage seats" />
              <HandoffButton label="Change plan" variant="secondary" />
            </div>
          </>
        )}

        {/* Above Business is contact-sales, never a self-serve button. */}
        <p className="pg-card__body">
          Need SSO, SCIM or a custom agreement?{" "}
          <a href={`${config.siteOrigin}/contact-sales`}>Contact sales</a>
        </p>
      </Card>

      <CancelSection state={state} />
    </div>
  );
}

function StatusBadge({ state }: { state: SubscriptionState }) {
  if (state.status === "active" && state.cancelAtPeriodEnd) {
    return <Badge tone="warn">ENDING SOON</Badge>;
  }
  if (state.status === "active") return <Badge tone="success">Active</Badge>;
  if (state.status === "trialing") return <Badge tone="success">Trialing</Badge>;
  if (state.status === "past_due") return <Badge tone="danger">Payment failed</Badge>;
  if (state.status === "locked") return <Badge tone="neutral">Trial ended</Badge>;
  if (state.status === "canceled") return <Badge tone="neutral">Ended</Badge>;
  return <Badge tone="neutral">Activating…</Badge>;
}

/**
 * Cancel — our reassurance copy FIRST, then the handoff to Stripe's confirm.
 *
 * No retention interstitial and no dark pattern: the trial was sold on "Cancel in one
 * click", and a cancel flow that argues with the customer is the fastest way to make that
 * sentence a lie.
 */
function CancelSection({ state }: { state: SubscriptionState }) {
  if (state.status === "canceled" || state.status === "locked") return null;

  if (state.cancelAtPeriodEnd) {
    return (
      <Card title="Your plan is ending">
        <Callout tone="warn" role="status">
          Your plan ends{" "}
          {state.currentPeriodEnd ? <DateText iso={state.currentPeriodEnd} /> : "at the end of the period"}.
          You keep full access until then, and nothing else will be charged.
        </Callout>
        <HandoffButton label="Resume" />
      </Card>
    );
  }

  if (state.status === "trialing") {
    return (
      <Card title="Cancel">
        <p className="pg-card__body">
          There&apos;s nothing to cancel — you have no card on file and nothing has been
          charged. Just walk away, or let the trial end.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Cancel your plan">
      <HandoffButton
        label="Cancel plan"
        variant="danger"
        confirm={{
          title: "Before you go",
          body: "You'll keep full access until the end of the period you've already paid for, and nothing more will be charged. Your invoices stay available. We never stored your prompts, so there's nothing to export.",
          proceed: "Continue to cancel",
        }}
      />
    </Card>
  );
}
